// Broadcasts — system-broadcast admin at /admin/broadcasts (PLEX-4102).
// List every broadcast (message, level, status), create new ones, edit
// message/level inline, and activate/deactivate. Backed by the broadcastApi
// admin endpoints; auth comes from the injected userStore.

import { useCallback, useEffect, useState } from "react";
import { inject, observer } from "mobx-react";

import {
  fetchAdminBroadcasts,
  createBroadcast,
  updateBroadcast,
  activateBroadcast,
  deactivateBroadcast,
} from "../../queries/broadcastApi";
import "./AdminBroadcasts.css";

const LEVELS = ["info", "warning", "critical"];

const StatusChip = ({ status }) => {
  const active = status === "active";
  return (
    <span className={`adminBc-chip adminBc-chip--${active ? "active" : "inactive"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
};

const LevelChip = ({ level }) => (
  <span className={`adminBc-level adminBc-level--${LEVELS.includes(level) ? level : "info"}`}>
    {level || "info"}
  </span>
);

// ─── Create form ────────────────────────────────────────────────────────────

const CreateForm = ({ onCreate, isPending }) => {
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState("info");
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!message.trim()) {
      setErr("Message is required");
      return;
    }
    try {
      await onCreate({ message: message.trim(), level });
      setMessage("");
      setLevel("info");
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  return (
    <div className="adminBc-form stone-card--subtle">
      <label className="adminBc-label adminBc-label--grow">
        <span className="adminBc-label__text">Message</span>
        <textarea
          className="adminBc-input adminBc-textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="System announcement shown to every user…"
          rows={2}
        />
      </label>
      <label className="adminBc-label">
        <span className="adminBc-label__text">Level</span>
        <select
          className="adminBc-input"
          value={level}
          onChange={(e) => setLevel(e.target.value)}
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </label>
      <button
        className="btn primary button-primary adminBc-create-btn"
        onClick={submit}
        disabled={isPending}
      >
        {isPending ? "Creating…" : "Create broadcast"}
      </button>
      {err && <div className="err adminBc-form__err">{err}</div>}
    </div>
  );
};

// ─── Inline edit row ──────────────────────────────────────────────────────────

const EditRow = ({ broadcast, onSubmit, onCancel, isPending }) => {
  const [message, setMessage] = useState(broadcast.message || "");
  const [level, setLevel] = useState(broadcast.level || "info");
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    const fields = {};
    if (message !== (broadcast.message || "")) fields.message = message;
    if (level !== (broadcast.level || "info")) fields.level = level;
    if (Object.keys(fields).length === 0) {
      setErr("No changes to save");
      return;
    }
    try {
      await onSubmit(broadcast.id, fields);
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  return (
    <tr>
      <td colSpan={4} className="adminBc-editcell">
        <div className="adminBc-form stone-card--subtle">
          <label className="adminBc-label adminBc-label--grow">
            <span className="adminBc-label__text">Message</span>
            <textarea
              className="adminBc-input adminBc-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
            />
          </label>
          <label className="adminBc-label">
            <span className="adminBc-label__text">Level</span>
            <select
              className="adminBc-input"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </label>
          <button className="btn primary button-primary" onClick={submit} disabled={isPending}>
            {isPending ? "Saving…" : "Save"}
          </button>
          <button className="btn ghost button-ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </button>
          {err && <div className="err adminBc-form__err">{err}</div>}
        </div>
      </td>
    </tr>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const Broadcasts = ({ userStore }) => {
  const authData = userStore?.authData;
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  // Separate from `pending` so saving a row does not also disable the Create
  // form — the two are independent admin actions.
  const [editPending, setEditPending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rowErr, setRowErr] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetchAdminBroadcasts(authData)
      .then((resp) => {
        const list = Array.isArray(resp?.broadcasts) ? resp.broadcasts : [];
        setBroadcasts(list);
      })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [authData]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async ({ message, level }) => {
    setPending(true);
    try {
      await createBroadcast(authData, { message, level });
      await load();
    } finally {
      setPending(false);
    }
  };

  const handleEdit = async (id, fields) => {
    // Mirrors handleCreate: without a real pending flag the Save button stayed
    // enabled through the request and a double-click fired duplicate PUTs.
    // The finally is not optional — resetting only on success would leave the
    // button disabled forever after a failed save.
    setRowErr(null);
    setEditPending(true);
    try {
      await updateBroadcast(authData, id, fields);
      setEditingId(null);
      await load();
    } catch (e) {
      // Stay in edit mode and surface the failure, matching handleActivate,
      // rather than closing the row and losing what was typed.
      setRowErr(`Save failed: ${e?.message || e}`);
    } finally {
      setEditPending(false);
    }
  };

  const handleActivate = async (b) => {
    setRowErr(null);
    try {
      await activateBroadcast(authData, b.id);
      await load();
    } catch (e) {
      setRowErr(`Activate failed: ${e?.message || e}`);
    }
  };

  const handleDeactivate = async (b) => {
    setRowErr(null);
    try {
      await deactivateBroadcast(authData, b.id);
      await load();
    } catch (e) {
      setRowErr(`Deactivate failed: ${e?.message || e}`);
    }
  };

  return (
    <>
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow section-eyebrow">Operations</div>
          <h1 className="serif hero-display">System Broadcasts</h1>
          <p className="lede">
            Broadcasts appear as a banner to every user of this cell. Activate one
            to raise it; deactivate to lower it. Users can dismiss a banner, and a
            dismissed banner stays hidden for them until a new one is raised.
          </p>
        </div>
        <div className="page-hero-actions">
          <button className="btn button-ghost" onClick={() => load()} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <CreateForm onCreate={handleCreate} isPending={pending} />

      {error && <div className="err">Error: {error}</div>}
      {rowErr && <div className="err">{rowErr}</div>}

      {broadcasts.length === 0 ? (
        <div className="adminBc-empty">
          {loading ? "Loading…" : "No broadcasts yet. Create one above to get started."}
        </div>
      ) : (
        <div className="stone-card adminBc-card">
          <table className="adminBc-table">
            <thead>
              <tr>
                <th>Message</th>
                <th>Level</th>
                <th>Status</th>
                <th className="adminBc-actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((b) => {
                if (editingId === b.id) {
                  return (
                    <EditRow
                      key={b.id}
                      broadcast={b}
                      onSubmit={handleEdit}
                      onCancel={() => setEditingId(null)}
                      isPending={editPending}
                    />
                  );
                }
                const active = b.status === "active";
                return (
                  <tr key={b.id}>
                    <td className="adminBc-message">{b.message}</td>
                    <td><LevelChip level={b.level} /></td>
                    <td><StatusChip status={b.status} /></td>
                    <td className="adminBc-actions">
                      <button
                        className="btn small ghost button-ghost"
                        data-testid={`edit-${b.id}`}
                        onClick={() => { setEditingId(b.id); }}
                        disabled={editingId !== null}
                      >
                        Edit
                      </button>
                      {active ? (
                        <button
                          className="btn small ghost button-ghost"
                          data-testid={`deactivate-${b.id}`}
                          onClick={() => handleDeactivate(b)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          className="btn small button-primary"
                          data-testid={`activate-${b.id}`}
                          onClick={() => handleActivate(b)}
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default inject("userStore")(observer(Broadcasts));
